import './Filter.less';

import React, { FC, useContext, useEffect, useMemo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { setSortBy, toggleFilterRadar, toggleFilterStream } from '../handlers';
import {
    initSport$,
    matchesCountBySportName$$,
    sportBySportIdFiltered$$,
    sportIdsOrdered$
} from '../push/overview/subs';
import { generatePath } from 'react-router-dom';
import { localizeMap } from '../utils';
import { SvgIcon } from '../../../icons/SvgIcon';
import clsx from 'clsx';
import { TURBO_LIVE_FAVORITE_PATH, TURBO_LIVE_SPORT_PATH } from '../../../common/routing';
import { SORT_BY_START_TIME, SORT_BY_TOURNAMENTS } from '../constants';
import { filterRadar$, filterShowCashin$, filterStream$, loggedIn$, sortBy$ } from '../subs';
import useIsRouteActive from '../../../common/routing/useIsRouteActive';
import { clearOverview } from '../push/overview/handlers';
import usePathMatch from '../../../common/routing/usePathMatch';
import { cashinAllToggle } from './cashin/handlers';
import TurboLiveContext from '../context/TurboLiveContext';
import { activeMatchTicketsInit } from './tickets/handlers';
import FilterDropdown from './FilterDropdown';
import i18n from '../../../common/i18n';
import { FilterButtonProps, FilterSwiper, NavigationFilterButton } from '../../filterSwiper/FilterSwiper';
import { myMatchesCount$ } from './myMatches/subs';
import { useLoadActiveTickets } from './tickets/hooks/useLoadActiveTickets';
import { useActiveMatchIdsPush } from '../push/overview/favorite/hooks';
import { isMobileChannel } from '../../../common/util';
import { SportIconNui } from '../../../icons/SportIconNui';

export const BTN_DEFAULT_CLASS = 'filter-btn br-radius-xs px-10 no-wrap';
const BTN_ACTIVE = 'bg-orange c-white filter-btn-active';
export const BTN_NOT_ACTIVE = 'filter-btn-default hover-bgr c-black-50';

const Filter: FC = () => {
    useActiveMatchIdsPush();
    useLoadActiveTickets();
    return <FilterView />;
};

const FilterView: FC = () => {
    const mobileChannel = isMobileChannel();

    const derefSub = useReframe();
    const sportIdsOrdered = derefSub(sportIdsOrdered$);

    const buttonElements = useMemo(
        () => [
            <FilterMyMatches key="matches" />,
            ...(sportIdsOrdered?.size
                ? sportIdsOrdered.toArray().map(sportId => <FilterSport key={sportId} sportId={sportId} />)
                : [<FilterSportsDummy key="dummy" />])
        ],
        [sportIdsOrdered]
    );

    return (
        <div
            className={clsx('filter flex flex-col c-default bg-white-ng', {
                'pt-2': mobileChannel,
                'p-rel': !mobileChannel
            })}
        >
            <FilterSwiper staticButton={<FilterDropdown />} refresh={sportIdsOrdered} buttons={buttonElements} />
        </div>
    );
};

const FilterSportsDummyView = () => (
    <div className="filter-slider-overflow flex-1 flex">
        {Array.from({ length: 20 }).map((e, i) => (
            <div className="filter-item filter-item-dummy bg-white-ng" key={i} />
        ))}
    </div>
);

const FilterSportsDummyMobile = () => {
    return <FilterSportsDummyView />;
};

const FilterSportsDummy = () => (isMobileChannel() ? <FilterSportsDummyMobile /> : <FilterSportsDummyView />);

const FilterSport: FC<{ sportId: string } & FilterButtonProps> = ({ sportId, activationChange }) => {
    const derefSub = useReframe();
    const sport = derefSub(sportBySportIdFiltered$$(sportId));
    const sportName = sport?.get('slug')?.replace('/', '');
    const defaultSportName = derefSub(initSport$);
    const matchesCount = derefSub(matchesCountBySportName$$(sportName));
    const path = generatePath(TURBO_LIVE_SPORT_PATH, { sportName });
    const { isExact, isParent, isChild } = usePathMatch(path);
    const active = isExact || isParent || (isChild && sportName === defaultSportName);
    const onClick = e => {
        !active && dispatch(clearOverview);
    };
    if (!sport) {
        return null;
    }
    const name = localizeMap(sport.get('sportName'));
    return (
        <NavigationFilterButton
            activationChange={activationChange}
            title={name}
            path={path}
            icon={<SportIconNui sportId={sport.get('id')} className={`flex-shrink-0`} />}
            active={active}
            badge={matchesCount > 0 && matchesCount}
            onClick={onClick}
        />
    );
};

const FilterMyMatches: FC = () => {
    const { showLoginModal } = useContext(TurboLiveContext);
    const derefSub = useReframe();
    const isUserLoggedIn = derefSub(loggedIn$);
    const count = derefSub(myMatchesCount$);
    const path = generatePath(TURBO_LIVE_FAVORITE_PATH);
    const badge = count ? count + '' : '';
    const active = useIsRouteActive(path);
    const onClick = e => {
        !active && isUserLoggedIn && dispatch(clearOverview);
        if (!isUserLoggedIn) {
            e.preventDefault();
            e.stopPropagation();
            showLoginModal(path);
        }
    };
    return count ? (
        <NavigationFilterButton
            title={i18n('nui.turbo.live.my-matches')}
            path={path}
            icon={<SvgIcon imgId="my-matches" sizeX="20" sizeY="20" className={`fill-my-matches flex-shrink-0`} />}
            badge={badge}
            active={active}
            activeBg="filter-my-matches"
            dataAtid="tl-menu-my-sports"
            onClick={onClick}
        />
    ) : null;
};

export const FilterByTimeBtn = () => {
    const derefSub = useReframe();
    const active = derefSub(sortBy$) === SORT_BY_START_TIME;
    const onClick = () => {
        const newSort = active ? SORT_BY_TOURNAMENTS : SORT_BY_START_TIME;
        dispatch(setSortBy(newSort));
    };
    return (
        <button
            data-atid="tlv-filter-time"
            data-tlv-filter-time={active}
            className={`${BTN_DEFAULT_CLASS} mr-15 ${active ? BTN_ACTIVE : BTN_NOT_ACTIVE}`}
            onClick={onClick}
        >
            {i18n('nui.turbo.live.by.time')}
        </button>
    );
};

export const FilterStreamBtn = () => {
    const derefSub = useReframe();
    const active = derefSub(filterStream$);
    const onClick = () => {
        dispatch(toggleFilterStream());
    };
    return (
        <button
            data-atid="tlv-filter-stream"
            data-tlv-filter-only-streams={active}
            className={`${BTN_DEFAULT_CLASS} mr-15 ${active ? BTN_ACTIVE : BTN_NOT_ACTIVE}`}
            onClick={onClick}
        >
            {i18n('nui.turbo.live.filter.streams')}
        </button>
    );
};

export const FilterRadarBtn = () => {
    const derefSub = useReframe();
    const active = derefSub(filterRadar$);
    const onClick = () => {
        dispatch(toggleFilterRadar);
    };
    return (
        <button
            data-atid="tlv-filter-radar"
            data-tlv-filter-only-radar={active}
            className={`${BTN_DEFAULT_CLASS} mr-15 ${active ? BTN_ACTIVE : BTN_NOT_ACTIVE}`}
            onClick={onClick}
        >
            {i18n('nui.turbo.live.filter.radar')}
        </button>
    );
};

export const CashinToggle: FC<{ mini?: boolean }> = ({ mini }) => {
    const derefSub = useReframe();
    const showCashin = derefSub(filterShowCashin$);
    useEffect(() => {
        dispatch(activeMatchTicketsInit);
    }, []);

    const onCashinClick = e => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(cashinAllToggle);
    };
    return (
        <>
            <button
                data-atid="tlv-cashin"
                data-tlv-filter-only-cashin={showCashin}
                className={clsx(
                    `flex-center ${BTN_DEFAULT_CLASS} ${showCashin || !mini ? BTN_NOT_ACTIVE : BTN_ACTIVE}`,
                    {
                        [isMobileChannel() ? ' filter-btn-lg' : 'filter-btn-md']: !mini
                    }
                )}
                onClick={onCashinClick}
            >
                {mini ? (
                    <SvgIcon
                        imgId="live-mobile-cashin"
                        sizeX="14"
                        sizeY="14"
                        className={`my-3 ${showCashin ? 'c-black-30' : 'c-white'}`}
                    />
                ) : showCashin ? (
                    i18n('nui.turbo.live.filter.cashins')
                ) : (
                    i18n('nui.turbo.live.filter.cashins.show')
                )}
            </button>
        </>
    );
};

export const FilterLabel: FC<{ children: any; otherClass?: string }> = ({ children, otherClass }) => (
    <span className={clsx('c-black-100 text-bold fs-14 pr-10 no-wrap', otherClass)}>{children}</span>
);

export default Filter;
